import styled from '@emotion/styled';
import { Button } from 'react-bootstrap';
import { ButtonVariant } from 'react-bootstrap/esm/types';
import { theme } from '../../../theme';
import { Label, LabelColor } from '../../others/Label';

type CardProps = {
  children?: React.ReactNode;
  className?: string;
  title?: string;
  description?: string;
  action?: {
    text: string;
    type: 'text' | 'button';
    buttonVariant?: ButtonVariant;
    onClick?: () => void;
  };
  label?: {
    text: string;
    color: LabelColor;
    subtle?: boolean;
  };
  markerColor?: LabelColor;
  dataIcom?: string;
};

export const ActionCard = ({
  children,
  className,
  title,
  description,
  action,
  label,
  markerColor,
  dataIcom,
}: CardProps) => {
  return (
    <CardWrapper className={className} data-cy="card" data-icom={dataIcom}>
      <CardMarker color={markerColor} data-cy="card-marker" />
      <CardContent>
        <HorizontalVerticalWrapper>
          <div>
            {title && <CardTitle data-cy="card-title">{title}</CardTitle>}
            <LabelAndDescriptionWrapper>
              {label && (
                <Label color={label.color} subtle={label.subtle}>
                  {label.text}
                </Label>
              )}
              {description && <CardDescription data-cy="card-description">{description}</CardDescription>}
            </LabelAndDescriptionWrapper>
          </div>
          <ButtonWrapper>
            {action && action.type === 'button' && (
              <Button
                variant={action.buttonVariant}
                onClick={action.onClick}
                size="sm"
                data-cy="card-action-button"
                data-icom="card-action-button"
              >
                {action.text}
              </Button>
            )}
            {action && action.type === 'text' && <ActionText data-cy="card-action-text">{action.text}</ActionText>}
          </ButtonWrapper>
        </HorizontalVerticalWrapper>
        {children && <ChildrenWrapper>{children}</ChildrenWrapper>}
      </CardContent>
    </CardWrapper>
  );
};

const CardWrapper = styled.div`
  display: flex;
  flex-direction: row;
  margin-top: ${theme.spacing.d};
  margin-bottom: ${theme.spacing.d};
`;

const CardMarker = styled.div<{ color?: LabelColor }>`
  width: ${theme.spacing.c};
  background-color: ${({ color }) => (color ? theme.colors[`emotion${color}`].default : 'white')};
  border-top-left-radius: ${theme.spacing.c};
  border-bottom-left-radius: ${theme.spacing.c};
`;

const CardContent = styled.div`
  flex-grow: 1;
  background-color: white;
  padding: ${theme.spacing.f} ${theme.spacing.g};
  border-top-right-radius: ${theme.spacing.c};
  border-bottom-right-radius: ${theme.spacing.c};
`;

const HorizontalVerticalWrapper = styled.div`
  display: flex;
  flex-direction: row;
  gap: ${theme.spacing.f};
  align-items: center;
  justify-content: space-between;
  @media (max-width: ${theme.breakpoint.small}) {
    flex-direction: column;
    align-items: flex-start;
  }
`;

const CardTitle = styled.h3({ ...theme.typography.heading.small.default, color: theme.colors.text.primary });

const LabelAndDescriptionWrapper = styled.div`
  .label {
    margin-right: ${theme.spacing.f};
    margin-bottom: ${theme.spacing.b};
  }
`;

const CardDescription = styled.span`
  color: ${theme.colors.text.secondary};
`;

const ActionText = styled.div`
  width: max-content;
  color: ${theme.colors.text.secondary};
`;

const ButtonWrapper = styled.div`
  button {
    width: max-content;
  }
  @media (max-width: ${theme.breakpoint.small}) {
    width: 100%;
    button {
      width: 100%;
    }
  }
`;

const ChildrenWrapper = styled.div`
  margin-top: ${theme.spacing.f};
`;
